import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import commonMessage from '@whitelabel/helpers/messages/commonMsg';
import { Loading } from '@whitelabel/component-library';
import { IUser, ICustomer } from '@whitelabel/helpers/types';
import { IError, ModalState, IHelpPagePartner } from '@whitelabel/xcover-shared/helpers/types';
import {
  StyledContactModal,
  StyledAlert,
  StyledLoadingCard,
} from '@whitelabel/xcover-shared/styled/StyledContactModal';
import { useZendeskChat } from '@whitelabel/xcover-shared/components/PartnerZendeskChat';
import { getPartners as getPartnersAction } from '../../actions';
import ContactPurpose from './ContactPurpose';
import ContactFormCard from './ContactFormCard';
import PartnersCard from './PartnersCard';
import ContactConfirmModal from './ContactConfirmModal';

interface IContactInfoModalProps {
  isOpen: boolean;
  toggleIsModalOpen: () => void;
  customer: ICustomer | undefined;
  getPartners(customerID: string): void;
  error?: IError;
  partners?: IHelpPagePartner[];
  user?: IUser;
  insPartner?: IHelpPagePartner;
  bookingID?: string;
}

const ContactModal = ({
  user,
  isOpen,
  toggleIsModalOpen,
  customer,
  partners,
  getPartners,
  error,
  insPartner,
  bookingID,
}: IContactInfoModalProps): null | JSX.Element => {
  const intl = useIntl();
  const [step, setStep] = useState<ModalState>('loading');
  const [partner, setPartner] = useState<IHelpPagePartner | undefined>(insPartner);
  const [singlePartner, setSinglePartner] = useState(!!insPartner);
  const { setZendeskChatPartners } = useZendeskChat();

  useEffect(() => {
    // call get partners always for Zendesk Chat
    if (user) {
      if (partners) {
        setZendeskChatPartners(partners);
      } else if (customer?.id) {
        setStep('loading');
        getPartners(customer.id);
      }
    }
  }, [customer?.id, getPartners, partners, user, setZendeskChatPartners]);

  useEffect(() => {
    if (partner) {
      setStep('single-policy');
      return;
    }

    if (user && partners) {
      if (partners.length === 1) {
        setPartner(partners[0]);
        setStep('single-policy');
        setSinglePartner(true);
        return;
      }
      if (partners.length > 1) {
        setStep('multi-policy');
      }
    }
  }, [customer, partners, partner, user]);

  const renderModalBody = () => {
    switch (step) {
      case 'loading':
        return (
          <StyledLoadingCard>
            <Loading />
          </StyledLoadingCard>
        );
      case 'multi-policy':
        return (
          <PartnersCard
            toggleModalClose={toggleIsModalOpen}
            partners={partners!}
            setPartner={setPartner}
            setStep={setStep}
          />
        );
      case 'single-policy':
        return (
          <ContactPurpose
            toggleModalClose={toggleIsModalOpen}
            partner={partner}
            setStep={setStep}
            enableReturn={!singlePartner}
          />
        );
      case 'contact-form':
        return (
          <ContactFormCard
            toggleModalClose={toggleIsModalOpen}
            partner={partner}
            setStep={setStep}
            bookingID={bookingID}
          />
        );
      // no default
    }
    return undefined;
  };

  if (step === 'confirmation') {
    return isOpen ? <ContactConfirmModal toggleModalClose={toggleIsModalOpen} isOpen={isOpen} /> : null;
  }

  return isOpen ? (
    <StyledContactModal $rtl={intl.bidi} isOpen={isOpen} toggle={toggleIsModalOpen} backdrop="static" size="md">
      {error && <StyledAlert color="danger">{intl.formatMessage(commonMessage.error)}</StyledAlert>}
      {renderModalBody()}
    </StyledContactModal>
  ) : null;
};

const mapStateToProps = (state: any) => {
  const { locale } = state.intl;
  const { data, error } = state.partners;

  return {
    customer: state.customer.data,
    partners: data[locale],
    error,
    user: state.user.data,
  };
};

const mapDispatchToProps = {
  getPartners: getPartnersAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactModal);
